<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, Dropdown, DropdownMenu, DropdownToggle, Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { CsvPreview } from '@client/components/csv-preview';
  import { FileInput } from '@client/components/file-input';
  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { csvStore } from '@client/stores/csv';
  import { usersSettingsStore } from '@client/stores/page-settings';

  import { readFile } from '@client/utils/csv';
  import { QueryParams } from '@client/utils/query-params';

  import { CSV_UPLOAD_OPTIONS } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  import CsvStatus from '../sync/csv-status.svelte';

  export let params = {};

  let sources = deepCopy(CSV_UPLOAD_OPTIONS);
  let selectedSource = '';
  let excludeUsers = false;
  let manualInput = '';

  const clearCSVFilters = (closeIcon) => {
    params = {
      ...params,
      csvfilter: {
        source: 'csv',
        raw: '',
        data: [],
        errors: [],
        manualInput: ''
      },
      excludeCSV: false
    };

    manualInput = '';

    if (closeIcon) {
      selectedSource = sources[0];
    }

    selectedSource.label = $format(selectedSource.label);

    resetCSVUpload();
  };

  const resetCSVUpload = () => {
    csvStore.setCsv('');
  };

  const handleFileInput = async (file) => {
    const csvText = await readFile(file);
    csvStore.setCsv(csvText);
  };

  const onSourceChange = () => {
    selectedSource.label = $format(selectedSource.label);

    if (!isDisabled) {
      clearCSVFilters();
    }
  };

  const onHandleManualEntry = (e) => {
    if (!e.target.value) {
      clearCSVFilters();
    }
    csvStore.setCsv(e.target.value);
  };

  const onFilterByCSV = () => {
    params = {
      ...params,
      excludeCSV: excludeUsers,
      csvfilter: {
        ...params.csvfilter,
        source: selectedSource.value,
        raw: $csvStore.raw,
        data: [...$csvStore.parsed.data],
        errors: [...$csvStore.parsed.errors],
        manualInput
      }
    };
  };

  const updateQueryParams = () => {
    const entries = [
      ['csvfilter', params?.csvfilter?.data.map((item) => item.username).join(',')],
      ['excludeCSV', params?.excludeCSV]
    ];

    for (const [key, value] of entries) {
      if (value) {
        QueryParams.set(key, value);
      } else {
        QueryParams.delete(key);
      }
    }
  };

  onMount(() => {
    const { params } = $usersSettingsStore;
    const csvfilter = params?.csvfilter ?? {};

    resetCSVUpload();

    selectedSource = csvfilter.source === 'manual' ? sources[1] : sources[0];
    selectedSource.label = $format(selectedSource.label);

    if (params?.excludeCSV) {
      excludeUsers = params.excludeCSV;
    }

    if (csvfilter?.raw) {
      csvStore.setCsv(csvfilter.raw);
    }

    if (csvfilter?.manualInput) {
      manualInput = csvfilter.manualInput;
    }
  });

  $: updateQueryParams(params.csvfilter.data, params.excludeCSV);

  $: isDisabled = !$csvStore.parsed?.data?.length || $csvStore.parsed?.errors?.length;
</script>

<Dropdown autoClose="outside">
  <DropdownToggle tag="div">
    <div class="filter-toggle" class:active={!isDisabled}>
      <span role="button" tabindex="0">
        <Icon name="file-text" class="pointer text-body-secondary" size={15} />
        {$format('label.CSV')}
        {#if !isDisabled}
          <div class="clear-icon" on:click|stopPropagation={clearCSVFilters} role="button" tabindex="0">
            <Icon name="x" />
          </div>
        {/if}
      </span>
    </div>
  </DropdownToggle>
  <DropdownMenu>
    <div class="csv-filter">
      <div class="section">
        <div class="section-info">
          <h5>
            {$format('label.SOURCE')}
            <Icon id="csv-info-tooltip" name="help-circle" />
            <Tooltip target="csv-info-tooltip">
              {$format('label.TOOLTIP.CSV_SOURCE')}
            </Tooltip>
          </h5>
        </div>
        <div class="section-controls">
          <Select
            id="sync-source"
            items={sources}
            bind:value={selectedSource}
            on:change={onSourceChange}
            placeholder={$format('label.PLACEHOLDER.SELECT_SYNC_SOURCE')}
            clearable={false}
            showChevron
            searchable
          />
        </div>
      </div>
      {#if selectedSource.value === 'csv'}
        <div class="section">
          <div class="section-info">
            <h5>
              {$format('label.SPREADSHEET_FILE')}
              <Icon id="csv-data-tooltip" name="help-circle" />
              <Tooltip target="csv-data-tooltip">{$format('label.TOOLTIP.SPREADSHEET_FILE')}</Tooltip>
            </h5>
          </div>
          <div class="section-controls">
            <FileInput
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              id="csv"
              onLoad={handleFileInput}
              onRemove={() => clearCSVFilters()}
            />
          </div>
        </div>
      {/if}

      {#if selectedSource.value === 'manual'}
        <div class="section">
          <div class="section-info">
            <h5>
              {$format('label.MANUAL_CSV')}
              <Icon id="manual-csv-tooltip" name="help-circle" />
              <Tooltip target="manual-csv-tooltip">{$format('label.TOOLTIP.MANUAL_CSV')}</Tooltip>
            </h5>
          </div>
          <div class="section-controls">
            <Input
              bind:value={manualInput}
              type="textarea"
              class="font-monospace"
              invalid={$csvStore.raw && $csvStore.parsed?.errors?.length}
              placeholder="username,name,title"
              rows={4}
              on:input={(e) => {
                onHandleManualEntry(e);
              }}
            />
          </div>
        </div>
      {/if}
      {#if $csvStore.raw && $csvStore.parsed}
        <div class="csv-preview">
          <CsvStatus
            class="status"
            errors={$csvStore.parsed.errors}
            headers={$csvStore.parsed.meta?.fields}
            rows={$csvStore.parsed.data}
            isFilter={true}
          />
          <CsvPreview
            errors={$csvStore.parsed.errors}
            headers={$csvStore.parsed.meta?.fields}
            rows={$csvStore.parsed.data}
          />
        </div>
      {/if}
      <div class="section">
        <div class="section-info">
          <h5>
            {$format('label.EXCLUDE_ENTRIES')}
            <Icon id="compliment-users-tooltip" name="help-circle" />
            <Tooltip target="compliment-users-tooltip">
              {@html $format('label.TOOLTIP.EXCLUDE_ENTRIES')}
            </Tooltip>
          </h5>
        </div>
        <div class="section-controls">
          <Switch bind:checked={excludeUsers} />
        </div>
      </div>
      <div class="footer-actions">
        <Button size="sm" color="secondary" disabled={isDisabled} on:click={onFilterByCSV}>
          {$format('label.APPLY_FILTER')}
        </Button>
      </div>
    </div>
  </DropdownMenu>
</Dropdown>

<style lang="scss" src="./csv-filter.scss"></style>
